import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, HostBinding, OnDestroy, OnInit, Renderer2, ViewContainerRef } from "@angular/core";
import { CDK_OVERLAY_BACKDROP_Z_INDEX_LEVEL1, WindowService } from "@merri/common";
import { Subject, merge } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/core";
import { DemoEventService } from "src/app/features/demo-event/services/demo-event.service";
import { HelperMethods } from "src/app/shared";
import { AssistantUiComponent, AssistantUiModel } from "../../models";
import { AssistantUiModelInstances } from "../../models/assistant-ui-model-instances";
import { AssistantService } from "../../services/assistant.service";

@Component({
    selector: "app-assistant-trigger",
    templateUrl: "./assistant-trigger.component.html",
    styleUrls: ["./assistant-trigger.component.scss"],
    standalone: false
})
export class AssistantTriggerComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  popupRef: OverlayRef;
  triggerText: string;
  isMobile: boolean;

  @HostBinding("class.hidden") get hidden() {
    return this.isOpen();
  }

  constructor(
    private assistantService: AssistantService,
    private sessionService: SessionService,
    private overlay: Overlay,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private windowService: WindowService,
    private demoEventService: DemoEventService, // a little test
  ) {}

  ngOnInit(): void {
    // debouncing avoids ExpressionChangedAfterItHasBeenCheckedError
    this.assistantService.open$.pipe(debounceTime(5), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.open();
      },
    });

    this.assistantService.close$.pipe(debounceTime(5), takeUntil(this.destroyed$)).subscribe({
      next: interactive => {
        this.close(interactive);
      },
    });

    this.sessionService.loggingOut$.pipe(debounceTime(5), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.close();

        this.setText(false);
      },
    });

    merge(this.sessionService.sessionUser$, this.windowService.oneTimeSmallScreen$)
      .pipe(debounceTime(5), takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.isMobile = this.windowService.isOneTimeSmallScreen();
          this.setText(this.userSupported());
        },
      });

    HelperMethods.delayRun(100, () => this.assistantService.raiseReady());

    this.assistantService.assistantUiOverrideModel$.pipe(debounceTime(5), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        const userSupported = this.userSupported();
        this.setText(userSupported);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.assistantService.raiseInteractive(false);
    this.assistantService.opened = false;
  }

  onTriggerClick(): void {
    if (!this.userSupported()) {
      if (this.assistantService.supportAgentOpened) {
        this.assistantService.hideSupportAgent();
      } else {
        this.assistantService.showSupportAgent();
      }
    } else if (this.popupRef) {
      this.close(true);
    } else {
      this.open(true);
    }
  }

  isOpen(): boolean {
    return !!this.popupRef || this.assistantService.supportAgentOpened;
  }

  private userSupported(): boolean {
    const activeAssistantUiModel = this._getActiveAssistantUiModel();

    return (
      !!this.demoEventService.getLoadedDemoEventId() && (activeAssistantUiModel.allowMobile || !this.windowService.isOneTimeSmallScreen())
    ); // a little test

    const userType = this.sessionService.getUserType();

    return (
      userType &&
      !this.sessionService.isGuestUser(userType) &&
      (activeAssistantUiModel.allowMobile || !this.windowService.isOneTimeSmallScreen())
    );
  }

  private open(interactive?: boolean): void {
    if (this.assistantService.supportAgentOpened) {
      this.assistantService.hideSupportAgent();
    }

    if (this.popupRef || !this.userSupported()) return;

    this._create();

    this.assistantService.opened = true;

    if (interactive) {
      this.assistantService.raiseInteractive(true);
    }
  }

  private _create() {
    const activeAssistantUiModel = this._getActiveAssistantUiModel();

    this.popupRef = this.overlay.create({
      positionStrategy: activeAssistantUiModel.positionStrategy,
      maxHeight: activeAssistantUiModel.maxHeight,
      width: activeAssistantUiModel.width,
      minHeight: activeAssistantUiModel.minHeight,
      height: activeAssistantUiModel.height,
      minWidth: activeAssistantUiModel.minWidth,
      maxWidth: activeAssistantUiModel.maxWidth,
    });

    this.renderer.addClass(this.popupRef.hostElement, CDK_OVERLAY_BACKDROP_Z_INDEX_LEVEL1);

    const portal = new ComponentPortal(activeAssistantUiModel.component, this.viewContainerRef);
    this.popupRef.attach(portal);
  }

  private _getActiveAssistantUiModel(): AssistantUiModel<AssistantUiComponent> {
    return this.assistantService.assistantUiOverrideModel || AssistantUiModelInstances.Popup;
  }

  private close(interactive?: boolean): void {
    if (!this.popupRef) return;

    this.popupRef.detach();
    this.popupRef = null;
    this.assistantService.opened = false;

    if (interactive) {
      this.assistantService.raiseInteractive(false);
    }
  }

  private setText(enabled: boolean): void {
    if (enabled) {
      this.triggerText = "Assistant";
    } else {
      this.triggerText = "Help";
    }
  }
}
